import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import 'url-search-params-polyfill';

import {
  AUTHENTICATION_HEADER,
  cleanAuth,
  getIdFromCookie,
  loadProfile,
  saveProfile,
  handleRedirect,
  isAccommodationProvider,
  getRole,
  isGuestUnauthenticated
} from '@spa/shared/utils/user';
import { redirect } from '@spa/shared/utils/redirect';
import { useStorage } from '@spa/shared/hooks/storage';
import { getEulerianRole } from '@spa/shared/utils/eulerian';
import { addAuthentication, deleteAuthentication, fetchGuest } from '@spa/shared/clients/core';

const UNAUTHORIZED_STATUS_CODE = 401;

export const useAuth = history => {
  const storage = useStorage('sessionStorage');
  const localStorage = useStorage('localStorage');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [cookies, setCookie, removeCookie] = useCookies([AUTHENTICATION_HEADER]);
  const [profile, setProfile] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const token = cookies[AUTHENTICATION_HEADER];
  const addUserSession = ({
    profile,
    id,
    wish_list,
    partnership,
    payment_info,
    registered,
    notification,
    documents
  }) => {
    const user = { ...profile, id, wish_list, partnership, payment_info, registered, notification, documents };

    saveProfile(storage, user);
    setProfile(user);
  };
  const fetchAndSaveUser = id => {
    if (!id) {
      cleanAuth(storage, localStorage, removeCookie);

      return;
    }

    fetchGuest(id)
      .then(addUserSession)
      .catch(({ response: { status } } = {}) => {
        if (status === UNAUTHORIZED_STATUS_CODE) {
          cleanAuth(storage, localStorage, removeCookie);
        }
      });
  };

  useEffect(() => {
    setProfile(loadProfile(storage));
    setIsLoadingProfile(false);
  }, [storage]);

  useEffect(() => {
    if (!cookies[AUTHENTICATION_HEADER]) {
      deleteAuthentication();
      setProfile(null);

      return;
    }

    addAuthentication(cookies[AUTHENTICATION_HEADER]);
    fetchAndSaveUser(getIdFromCookie(cookies, setCookie));
  }, [cookies[AUTHENTICATION_HEADER]]);

  return {
    profile,
    setProfile,
    isLoadingProfile,
    isAccommodationProvider: isAccommodationProvider(token),
    isAuthenticating,
    isLoggedIn: !!token && !isGuestUnauthenticated(token),
    isGuestUnauthenticated: isGuestUnauthenticated(token),
    eulerianRole: getEulerianRole(getRole(cookies[AUTHENTICATION_HEADER])),
    withLoading: action => (...args) => {
      setIsAuthenticating(true);

      return Promise.resolve(action(...args))
        .then(response => {
          if (response.errors) {
            setIsAuthenticating(false);
          }

          return response;
        })
        .catch(_ => setIsAuthenticating(false));
    },
    setAuth: ({ token }, isSignup = false, needsRedirect = true) => {
      cleanAuth(storage, localStorage, removeCookie);

      setIsAuthenticating(true);
      setCookie(AUTHENTICATION_HEADER, token, {
        domain: process.env.COOKIE_DOMAIN,
        path: '/'
      });

      if (needsRedirect || isAccommodationProvider(token)) {
        handleRedirect(redirect(history), token, isSignup);
      }

      setIsAuthenticating(false);
    },
    unsetAuth: () => {
      cleanAuth(storage, localStorage, removeCookie);
    }
  };
};
